import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Optional, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UnifiedAuthService } from '../../../services/unified-auth.service';
import { UserLoginResponse } from '../../../services/responses/pessoa.response';
import { MatButtonModule } from '@angular/material/button';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AlertService, AlertType } from '../../../services/alert.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { UtilService } from '../../../services/util.service';
import { Router } from '@angular/router';
import { RoutesService } from '../../../../routes.service';
import { PreferencesService } from '../../../../preferences.service';
import { MatIconModule } from '@angular/material/icon';
import { animate, style, transition, trigger } from '@angular/animations';
import { OneSelectComponent, OneSelectOption } from '../../one-field/one-select/one-select.component';
import { OneBadgeComponent } from '../../one-badge/one-badge.component';
import { DistribuidorStruct } from '../../../services/structs/admin/estoque.admin.struct';
import { iconeBadgeTipo, labelTipoDistribuidor, rotuloNoParaSelecao, varianteBadgeTipo } from '../../../../admin/rede/tipo-distribuidor.helpers';
import { GuiaOneService } from '../../../services/tour-mode/guia-one.service';

@Component({
    selector: 'app-action-user',
    imports: [
    MatButtonModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatIconModule,
    OneSelectComponent,
    OneBadgeComponent
],
    templateUrl: './action-user.component.html',
    styleUrl: './action-user.component.css',
    animations: [
        trigger('fadeScale', [
            transition(':enter', [
                style({ opacity: 0, transform: 'scale(0.95) translateY(-8px)' }),
                animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'scale(1) translateY(0)' })),
            ]),
            transition(':leave', [
                animate('100ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0.95) translateY(-8px)' })),
            ]),
        ]),
    ]
})
export class ActionUserComponent implements OnInit {
  @Input() showActionUser: boolean;
  @Output() showActionUserFalse = new EventEmitter<number>();
  public userLoginResponse: UserLoginResponse;
  private ip: string = '192.168.0.2';
  private password: string = '******';
  public idEmpresaLogada: number;
  public model: FormGroup;
  public isLoading: boolean = false;
  currentTheme: 'light' | 'dark' = 'light';
  public isAdminContext: boolean = false;
  public showEnterpriseSection: boolean = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly guia = inject(GuiaOneService);

  /** Item "Tour" / "Tour desta página" (2026-11-05) — o guia decide rótulo e estado; `null` = sem
   *  item. Só o bloco do ERP renderiza: o admin não tem tour. */
  readonly acaoTour = this.guia.acaoDoMenu;

  get empresaOptions(): OneSelectOption[] {
    return (this.userLoginResponse?.listEmpresaStruct ?? []).map((empresa) => ({
      value: empresa.idEmpresa,
      label: empresa.nomeEmpresa,
    }));
  }

  get distribuidorOptions(): OneSelectOption[] {
    const lista: DistribuidorStruct[] = (this.userLoginResponse as any)?.listDistribuidorStruct ?? [];
    return lista.map(d => ({ value: d.idDistribuidor, label: rotuloNoParaSelecao(d, lista) }));
  }

  /** Nó logado no admin (badge do header). */
  get noLogado(): DistribuidorStruct | null {
    return (this.userLoginResponse as any)?.distribuidorLogado ?? null;
  }
  readonly labelTipo = labelTipoDistribuidor;
  readonly iconeTipo = iconeBadgeTipo;
  readonly varianteTipo = varianteBadgeTipo;

  /** Iniciais do nome do usuário (até 2 letras) */
  get userInitials(): string {
    const name = this.userLoginResponse?.nomeUsuario ?? '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (parts[0]?.[0] ?? '?').toUpperCase();
  }

  /** Primeiro nome do usuário */
  get firstName(): string {
    const name = this.userLoginResponse?.nomeUsuario ?? '';
    return name.trim().split(/\s+/)[0] ?? '';
  }

  /** Nome da empresa logada */
  get empresaLogadaNome(): string {
    return this.userLoginResponse?.empresaLogada?.nomeEmpresa ?? '';
  }

  constructor(
    private authService: UnifiedAuthService,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private utilService: UtilService,
    private router: Router,
    @Optional() private routesService: RoutesService,
    private preferencesService: PreferencesService
  ) { }

  ngOnInit(): void {
    this.isAdminContext = this.router.url.startsWith('/admin') ||
      (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.'));

    this.model = this.formBuilder.group({
      enterpriseSelect: ['', [Validators.required]],
    });
    this.userLoginResponse = this.authService.getUserInfos();

    if (this.isAdminContext) {
      const response = this.userLoginResponse as any;
      this.model
        .get('enterpriseSelect')
        ?.setValue(response.distribuidorLogado?.idDistribuidor, { emitEvent: false });
    } else {
      this.model
        .get('enterpriseSelect')
        ?.setValue(this.userLoginResponse.empresaLogada?.idEmpresa, { emitEvent: false });
    }

    // one-select não emite (change) de DOM; reescuta troca via valueChanges.
    // Valor inicial setado com emitEvent:false acima para não disparar o reload.
    this.model.get('enterpriseSelect')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.changeEnterprise(null));

    this.getIP();
  }

  toggleTheme() {
    this.preferencesService.toggleTheme();
    this.currentTheme = this.preferencesService.getTheme();
  }

  navigateTo(route: string): void {
    this.showActionUser = false;
    if (this.routesService && !this.isAdminContext) {
      this.routesService.smartNavigate(route);
    } else {
      this.router.navigate([route]);
    }
  }

  /** Fecha o menu ANTES de agir (o tour não pode nascer por baixo do dropdown). O guard de
   *  `desabilitada` é do método, não só do atributo `disabled` do botão. */
  clickTour(): void {
    const acao = this.acaoTour();
    if (!acao || acao.desabilitada) return;
    this.closeMobile();
    void this.guia.executar(acao.id);
  }

  closeMobile(): void {
    this.showActionUser = false;
    this.showActionUserFalse.emit();
  }

  clickContainerOut(): void {
    this.showActionUserFalse.emit();
  }

  async clickLogout(): Promise<void> {
    await this.preferencesService.flushIfDirty();
    this.authService.logout();
  }

  changeEnterprise(_event?: any) {
    const idSelecionado = this.model.get('enterpriseSelect')?.value;
    if (idSelecionado == null || idSelecionado === '') return;

    if (this.isAdminContext) {
      this.changeEnterpriseAdmin(idSelecionado);
    } else {
      this.changeEnterpriseApp(idSelecionado);
    }
  }

  private changeEnterpriseApp(idNovaEmpresa: number) {
    // Verificação de Horário
    const podeEntrar = this.authService.verificarAcessoEmpresaNoLogin(
      this.userLoginResponse,
      idNovaEmpresa
    );

    if (!podeEntrar) {
      this.alertService.show(
        'Atenção!',
        'Esta empresa está fora do horário de expediente.',
        AlertType.warning
      );

      setTimeout(() => {
        window.location.reload();
      }, 500);

      return;
    }

    this.isLoading = true;
    this.idEmpresaLogada = idNovaEmpresa;

    this.authService
      .generateToken(
        this.userLoginResponse.nomeUsuario,
        this.password,
        this.ip,
        this.idEmpresaLogada,
        this.userLoginResponse.token
      )
      .subscribe({
        next: (response: UserLoginResponse) => {
          if (response.isError) {
            this.alertService.show(
              'Ops!',
              response.errorDescription,
              AlertType.warning
            );
            this.isLoading = false;
            setTimeout(() => window.location.reload(), 1000);
            return;
          }
          this.authService.updateToken(response);
          if (this.routesService) {
            this.routesService.resetRoutes(response);
          }
          this.preferencesService.setLogoSistema(
            response.empresaLogada.preferenciaEmpresaStruct.urlLogoEmpresa
          );
          this.preferencesService.setCorSistema(
            response?.empresaLogada.preferenciaEmpresaStruct.corHex,
            response?.empresaLogada.preferenciaEmpresaStruct.corHoverHex
          );
          this.preferencesService.setCompanyState(this.idEmpresaLogada);

          this.authService.confirmarAcessoEmpresa(this.idEmpresaLogada);

          this.isLoading = false;
          location.reload();
        },
        error: (error) => {
          this.alertService.show('Ops!', error, AlertType.warning);
          this.isLoading = false;
          return;
        },
      });
  }

  private changeEnterpriseAdmin(idDistribuidor: number) {
    this.isLoading = true;
    this.idEmpresaLogada = idDistribuidor;

    this.authService
      .generateToken(
        this.userLoginResponse.nomeUsuario,
        this.password,
        this.ip,
        idDistribuidor,
        this.userLoginResponse.token
      )
      .subscribe({
        next: (response: any) => {
          if (response.isError) {
            this.alertService.show(
              'Erro inesperado',
              response.errorDescription,
              AlertType.error
            );
            this.isLoading = false;
            return;
          }
          this.authService.updateToken(response);
          this.preferencesService.setLogoSistema(
            response.distribuidorLogado?.preferenciaDistribuidorStruct?.urlLogoEmpresa
          );
          this.preferencesService.setCorSistema(
            response?.distribuidorLogado?.preferenciaDistribuidorStruct?.corHex,
            response?.distribuidorLogado?.preferenciaDistribuidorStruct?.corHoverHex
          );
          this.preferencesService.setCompanyState(idDistribuidor);
          this.isLoading = false;
          location.reload();
        },
        error: (error) => {
          this.alertService.show('Erro inesperado', error, AlertType.error);
          this.isLoading = false;
          return;
        },
      });
  }

  getIP() {
    this.utilService.getIP().subscribe({
      next: (response) => {
        if (response && response.ip) {
          this.ip = response.ip;
        }
      },
    });
  }

  redirectDataConfig() {
    this.router.navigate(['config/meus-dados']);
  }
  redirectCompanyConfig() {
    this.router.navigate(['config/minha-empresa']);
  }
  redirectUsersConfig() {
    this.router.navigate(['config/usuarios']);
  }
}
